<template>
  <surface-dialog-desktop-app-required v-if="!supported" :feature="__('video recording')" @close="cancel" />
  <div v-else class="video-recorder modal panel panel-top" @click.self="cancel">
    <div class="panel-container is-animated slide-from-top">
      <template>
        <div v-if="uploading">
          <header class="panel-header" />
          <div class="panel-body" :style="{ height: canvasHeight + 'px' }">
            <div class="panel-interstitial">
              <interstitial-uploader
                :file="file"
                :download="true"
                :max-file-size="maxFileUploadSize"
                :title="__('Saving video')"
                @done="onUploadDone"
                @cancel="onUploadCancel"
              />
            </div>
          </div>
          <footer class="panel-footer" />
        </div>
        <video-replay
          v-else-if="playback"
          :src="playbackURL"
          :height="canvasHeight"
          :width="canvasWidth"
          @close="stopPlayback"
          @done="done"
        />
        <!-- using show instead of if here so that the state is maintained when we go back -->
        <video-booth
          v-show="!playback && !uploading"
          ref="booth"
          :height="canvasHeight"
          :width="canvasWidth"
          @done="startPlayback"
          @close="cancel"
          @stream="saveStreamInfo"
        />
      </template>
    </div>
  </div>
</template>

<script>
/* eslint-disable @typescript-eslint/explicit-function-return-type */
import ContentSourceUploaderMixin from 'vuemixins/content_source_uploader_mixin'
import EscapeToCancelMixin from 'vuemixins/escape_to_cancel_mixin'
import VideoBooth from 'vuecomponents/video_booth'
import VideoReplay from 'vuecomponents/video_replay'
import SurfaceDialogDesktopAppRequired from 'vuecomponents/surface_dialog_desktop_app_required'
import { supportedVideoExtension, supportsRecording, supportedVideoCodec, supportedAudioCodec } from 'bits/av_recorder'
import { BYTES_FOR_5_MIN_VIDEO_RECORDING } from 'bits/numbers'
import { captureMessage } from 'bits/error_tracker'

export default {
  components: { VideoBooth, VideoReplay, SurfaceDialogDesktopAppRequired },
  mixins: [ContentSourceUploaderMixin, EscapeToCancelMixin],
  data() {
    return {
      playback: false,
      blob: null,
      canvasWidth: 640,
      canvasHeight: 360,
      supported: supportsRecording,
      maxFileUploadSize: BYTES_FOR_5_MIN_VIDEO_RECORDING,
    }
  },
  computed: {
    playbackURL() {
      return this.blob ? URL.createObjectURL(this.blob) : null
    },
  },
  watch: {
    playbackURL(newValue, oldValue) {
      if (oldValue) {
        URL.revokeObjectURL(oldValue)
      }
    },
  },
  beforeDestroy() {
    if (this.playbackURL) {
      URL.revokeObjectURL(this.playbackURL)
    }
  },
  methods: {
    cancel() {
      if (!this.supported) {
        this.close()
      } else if (!this.$refs.booth.hasChunks) {
        this.close()
      } else if (
        window.confirm(this.__('You have not saved your video. Are you sure you want to close video booth?'))
      ) {
        this.close()
      }
    },
    close() {
      this.$emit('close')
    },
    done(title) {
      const finalTitle = title || 'video'
      if (!supportedVideoExtension) {
        captureMessage('Unable to get supported video extension for recorder', {
          context: {
            supportedVideoCodec,
            supportedAudioCodec,
          },
        })
      }
      const file = new File([this.blob], `${finalTitle}.${supportedVideoExtension}`, { type: this.blob.type })
      this.handleCaption(title)
      this.handleFile(file)
    },
    saveStreamInfo(streamInfo) {
      this.canvasHeight = streamInfo.canvasHeight
    },
    startPlayback(blob) {
      this.playback = true
      this.blob = blob
    },
    stopPlayback() {
      this.playback = false
    },
  },
}
</script>

<style lang="scss" scoped>
.button-list {
  justify-content: center;
}
.panel-body {
  transition: height 0.2s;
}
</style>
